import React, { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import last from 'lodash/last';
import countBy from 'lodash/countBy';
import { Box, Button, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import LockIcon from '@mui/icons-material/Lock';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

import { DataContext } from '../data/DataContext';
import { DataContextType } from '../data_types/interfaces';
import { formatHistoryDateVerbose } from '../data/formatting';
import { placeLabel, placeNumber } from '../data/player_insights';
import { MyRound, courseSpans, formatToPar } from '../data/scorecard_insights';
import MatchCardModal from '../shared/MatchCardModal';
import { ScoreMark, ScoreMarkLegend } from '../shared/ScoreMark';
import useMyRounds from '../shared/useMyRounds';
import { DS, Overline, Panel, formatSigned, numericSx, trendColor } from '../shared/Dashboard';

const toParColor = (toPar: number) => trendColor(-toPar);

const placeColor = (position: string) => {
    switch (placeNumber(position)) {
        case 1: return DS.gold;
        case 2: return DS.silver;
        case 3: return DS.bronze;
        default: return DS.text;
    }
};

const Stat: React.FC<{ label: string; value: React.ReactNode; sub?: React.ReactNode }> = ({ label, value, sub }) => (
    <Box>
        <Overline>{label}</Overline>
        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: DS.text, mt: 0.25, ...numericSx }}>{value}</Typography>
        {sub && <Typography sx={{ fontSize: '12px', color: DS.textMuted, ...numericSx }}>{sub}</Typography>}
    </Box>
);

const holeGridSx = (holes: number) => ({
    display: 'grid',
    gridTemplateColumns: `48px repeat(${holes}, minmax(30px, 1fr))`,
    alignItems: 'center',
    rowGap: 0.75,
    minWidth: 48 + holes * 32,
});

const rowLabelSx = { fontSize: '11px', fontWeight: 700, color: DS.textFaint, textTransform: 'uppercase' as const, letterSpacing: '0.06em' };

/** Hole-by-hole strip: hole number, par and the player's marked score. */
const HoleStrip: React.FC<{ myRound: MyRound }> = ({ myRound }) => {
    const { holes, card } = myRound;
    const counts = countBy(card.scores, s => {
        if (s.strokes === null || s.toPar === null) return 'none';
        if (s.toPar < 0) return 'under';
        if (s.toPar === 0) return 'par';
        return 'over';
    });
    const summary = [
        counts.under && `${counts.under} under par`,
        counts.par && `${counts.par} par${counts.par !== 1 ? 's' : ''}`,
        counts.over && `${counts.over} over par`,
    ].filter(Boolean).join(' · ');

    return (
        <Box>
            <Box sx={{ overflowX: 'auto', pb: 0.5 }}>
                <Box sx={holeGridSx(holes.length)}>
                    <Typography sx={rowLabelSx}>Hole</Typography>
                    {holes.map((h, i) => (
                        <Typography key={i} sx={{ fontSize: '12px', fontWeight: 600, color: DS.textMuted, textAlign: 'center', ...numericSx }}>{h.holeNum}</Typography>
                    ))}
                    <Typography sx={rowLabelSx}>Par</Typography>
                    {holes.map((h, i) => (
                        <Typography key={i} sx={{ fontSize: '12px', color: DS.textFaint, textAlign: 'center', ...numericSx }}>{h.par ?? '–'}</Typography>
                    ))}
                    <Typography sx={rowLabelSx}>You</Typography>
                    {card.scores.map((s, i) => <ScoreMark key={i} cell={s} size={30} />)}
                </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                <ScoreMarkLegend />
                {summary && <Typography sx={{ fontSize: '12px', color: DS.textMuted, ...numericSx }}>{summary}</Typography>}
            </Box>
        </Box>
    );
};

/** Shown instead of the hole-by-hole strip for players without DGV Club. */
const LockedHoleStrip: React.FC<{ holes: number }> = ({ holes }) => {
    const navigate = useNavigate();
    return (
        <Box sx={{ position: 'relative', borderRadius: '12px', overflow: 'hidden' }}>
            {/* Placeholder marks only, so the real scores never reach the page */}
            <Box aria-hidden sx={{ ...holeGridSx(holes), opacity: 0.25, filter: 'blur(1px)', py: 1 }}>
                <Box />
                {Array.from({ length: holes }, (_, i) => (
                    <Box key={i} sx={{ width: 30, height: 30, mx: 'auto', borderRadius: i % 3 ? '4px' : '50%', border: `1.5px solid ${DS.textMuted}` }} />
                ))}
            </Box>
            <Box sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
                px: 2,
                background: alpha(DS.surface, 0.55),
            }}>
                <LockIcon sx={{ fontSize: 18, color: DS.warning }} />
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: DS.text }}>
                    Hole-by-hole scorecards are part of DGV Club
                </Typography>
                <Button
                    size="small"
                    onClick={() => navigate('/dgv_club')}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: '8px',
                        color: DS.warning,
                        background: alpha(DS.warning, 0.12),
                        '&:hover': { background: alpha(DS.warning, 0.2) },
                    }}
                >
                    See DGV Club
                </Button>
            </Box>
        </Box>
    );
};

const LastRound = () => {
    const context = useContext<DataContextType | undefined>(DataContext);
    const { isClubMember } = context || {};
    const { rounds, summary } = useMyRounds();
    const [cardOpen, setCardOpen] = useState(false);

    const myRound = last(rounds);
    const spans = useMemo(() => (myRound ? courseSpans(myRound.holes) : []), [myRound]);

    if (!myRound) return null;

    const { round, card, holes, knownPar, match } = myRound;
    const totalPar = holes.reduce((sum, h) => sum + (h.par ?? 0), 0);
    const isBest = summary.best === myRound;
    // A retired round's to-par only covers the holes played, so show strokes instead
    const showToPar = knownPar && !card.retired;

    return (
        <>
            <Panel
                title="Last round"
                subtitle={`Round #${round.matchNum} · ${formatHistoryDateVerbose(round.date)}${spans.length ? ` · ${spans.map(s => s.course).join(', ')}` : ''}`}
                delay={0.05}
                action={isClubMember && (
                    <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        startIcon={<OpenInFullIcon sx={{ fontSize: '14px !important' }} />}
                        onClick={() => setCardOpen(true)}
                        sx={{
                            borderColor: DS.borderStrong,
                            color: DS.text,
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: '10px',
                            '&:hover': { borderColor: DS.brand, background: alpha(DS.brand, 0.08) },
                        }}
                    >
                        Full scorecard
                    </Button>
                )}
            >
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'auto 1fr' },
                    gap: { xs: 2.5, md: 4 },
                    alignItems: 'center',
                }}>
                    {/* Free for everyone: the total */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                        <Box>
                            <Overline>Score</Overline>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25 }}>
                                <Typography sx={{ fontSize: '48px', fontWeight: 800, lineHeight: 1, letterSpacing: '-0.03em', color: showToPar ? toParColor(card.toPar) : DS.text, ...numericSx }}>
                                    {showToPar ? formatToPar(card.toPar) : card.strokes}
                                </Typography>
                                {isBest && (
                                    <Box component="span" sx={{ px: 1, py: '2px', borderRadius: '999px', background: alpha(DS.gold, 0.14), color: DS.gold, fontSize: '11px', fontWeight: 700 }}>
                                        Season best
                                    </Box>
                                )}
                                {card.retired && (
                                    <Box component="span" sx={{ px: 1, py: '2px', borderRadius: '999px', background: alpha(DS.loss, 0.14), color: DS.loss, fontSize: '11px', fontWeight: 700 }}>
                                        Retired
                                    </Box>
                                )}
                            </Box>
                            <Typography sx={{ fontSize: '13px', color: DS.textMuted, mt: 0.75, ...numericSx }}>
                                {card.strokes} strokes{showToPar ? ` · par ${totalPar}` : ''}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, auto)', columnGap: 3, rowGap: 1.5 }}>
                            <Stat
                                label="Finish"
                                value={<span style={{ color: placeColor(String(round.position)) }}>{placeLabel(String(round.position))}</span>}
                                sub={`of ${match.cardSize}`}
                            />
                            <Stat
                                label="Rating"
                                value={<span style={{ color: trendColor(round.ratingChange) }}>{formatSigned(round.ratingChange)}</span>}
                                sub={`now ${round.currentRating}`}
                            />
                            <Stat
                                label="Season avg"
                                value={summary.averageToPar === null
                                    ? '—'
                                    : <span style={{ color: toParColor(summary.averageToPar) }}>{formatToPar(summary.averageToPar, 1)}</span>}
                                sub={`over ${summary.rounds} rounds`}
                            />
                            <Stat
                                label="Season best"
                                value={summary.best
                                    ? <span style={{ color: toParColor(summary.best.card.toPar) }}>{formatToPar(summary.best.card.toPar)}</span>
                                    : '—'}
                                sub={summary.best ? `Round #${summary.best.round.matchNum}` : undefined}
                            />
                        </Box>
                    </Box>

                    {/* DGV Club: the hole-by-hole card */}
                    <Box sx={{ minWidth: 0 }}>
                        {isClubMember ? <HoleStrip myRound={myRound} /> : <LockedHoleStrip holes={holes.length || 9} />}
                    </Box>
                </Box>
            </Panel>

            {isClubMember && (
                <MatchCardModal
                    matchData={cardOpen ? { ...round, card: match } : null}
                    isOpen={cardOpen}
                    onClose={() => setCardOpen(false)}
                />
            )}
        </>
    );
};

export default LastRound;
